// ========== CASHIER MESSAGING COMPONENT ==========
// Allows cashiers to send messages to admin and view their inbox
import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Modal, 
  Input, 
  List, 
  Badge, 
  Space, 
  Typography, 
  Empty,
  Tag,
  notification 
} from 'antd';
import { 
  InboxOutlined, 
  SendOutlined 
} from '@ant-design/icons';
import { database, ref, push, set, onValue, get } from '../../pages/Auth/db/firebase';

const { TextArea } = Input;
const { Text } = Typography;

const CashierMessaging = ({ facilityId, cashierData }) => {
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [inboxModalOpen, setInboxModalOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [api, contextHolder] = notification.useNotification();

  const cashierId = cashierData?.uid || cashierData?.id;
  const cashierName = cashierData?.username || cashierData?.name || 'Cashier';

  // ========== LISTEN TO INBOX MESSAGES ==========
  useEffect(() => {
    if (!facilityId || !cashierId) return;

    const messagesRef = ref(database, `messages/${facilityId}`);
    
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const messagesList = Object.entries(data)
          .map(([id, msg]) => ({ id, ...msg }))
          .filter(msg => 
            (msg.to === 'cashier' && msg.toId === cashierId) || 
            (msg.from === 'cashier' && msg.fromId === cashierId)
          )
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        setMessages(messagesList);
        
        // Count unread messages sent to this cashier
        const unread = messagesList.filter(m => 
          m.status === 'unread' && m.to === 'cashier' && m.toId === cashierId
        ).length;
        setUnreadCount(unread);
      } else {
        setMessages([]);
        setUnreadCount(0);
      }
    });

    return () => unsubscribe();
  }, [facilityId, cashierId]);

  // ========== SEND MESSAGE TO ADMIN ==========
  const handleSendToAdmin = async () => {
    if (!helpMessage.trim()) {
      api.warning({ message: 'Empty Message', description: 'Please enter a message.' });
      return;
    }

    setSending(true);
    try {
      const messagesRef = ref(database, `messages/${facilityId}`);
      const newMessageRef = push(messagesRef);
      
      await set(newMessageRef, {
        from: 'cashier',
        fromId: cashierId,
        fromName: cashierName,
        to: 'admin',
        message: helpMessage,
        timestamp: new Date().toISOString(),
        status: 'unread',
        facilityId
      });

      api.success({ 
        message: 'Message Sent', 
        description: 'Your message has been sent to the admin.' 
      });
      
      setHelpMessage('');
      setHelpModalOpen(false);
    } catch (error) {
      console.error('Error sending message:', error);
      api.error({ 
        message: 'Send Failed', 
        description: error.message 
      });
    } finally {
      setSending(false);
    }
  };

  // ========== MARK MESSAGE AS READ ==========
  const markAsRead = async (messageId) => {
    try {
      const messageRef = ref(database, `messages/${facilityId}/${messageId}`);
      const snapshot = await get(messageRef);
      
      if (snapshot.exists()) {
        const msgData = snapshot.val();
        await set(messageRef, {
          ...msgData,
          status: 'read'
        });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // ========== REPLY TO MESSAGE ==========
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setSending(true);
    try {
      const messagesRef = ref(database, `messages/${facilityId}`);
      const newMessageRef = push(messagesRef);
      
      await set(newMessageRef, {
        from: 'cashier',
        fromId: cashierId,
        fromName: cashierName,
        to: 'admin',
        message: replyText,
        timestamp: new Date().toISOString(),
        status: 'unread',
        facilityId,
        inReplyTo: replyingTo?.id
      });

      api.success({ message: 'Reply Sent' });
      setReplyText('');
      setReplyModalOpen(false);
      setReplyingTo(null);
    } catch (error) {
      console.error('Error sending reply:', error);
      api.error({ message: 'Send Failed', description: error.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {contextHolder}
      
      {/* Message and Inbox Buttons */}
      <Space>
        <Button
          icon={<SendOutlined />}
          onClick={() => setHelpModalOpen(true)}
        >
          Message Admin
        </Button>
        
        <Badge count={unreadCount} offset={[-5, 5]}>
          <Button
            icon={<InboxOutlined />}
            onClick={() => setInboxModalOpen(true)}
          >
            Inbox
          </Button>
        </Badge>
      </Space>

      {/* Send Message Modal */}
      <Modal
        title={<Space><SendOutlined /> Message Admin</Space>}
        open={helpModalOpen}
        onCancel={() => setHelpModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setHelpModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSendToAdmin}
          >
            Send Message
          </Button>
        ]}
      >
        <TextArea
          rows={6}
          placeholder="Type your message to the admin..."
          value={helpMessage}
          onChange={(e) => setHelpMessage(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>

      {/* Inbox Modal */}
      <Modal
        title={<Space><InboxOutlined /> Messages</Space>}
        open={inboxModalOpen}
        onCancel={() => setInboxModalOpen(false)}
        footer={null}
        width={700}
      >
        {messages.length === 0 ? (
          <Empty description="No messages" />
        ) : (
          <List
            dataSource={messages}
            renderItem={(msg) => (
              <List.Item
                key={msg.id}
                onClick={() => {
                  if (msg.status === 'unread' && msg.to === 'cashier' && msg.toId === cashierId) {
                    markAsRead(msg.id);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  background: msg.status === 'unread' && msg.to === 'cashier' && msg.toId === cashierId ? 'var(--bg-tertiary)' : 'transparent',
                  padding: '12px',
                  borderRadius: '8px'
                }}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={msg.from === 'cashier' && msg.fromId === cashierId ? 'blue' : 'green'}>
                        {msg.from === 'cashier' && msg.fromId === cashierId ? 'You' : msg.fromName || 'Admin'}
                      </Tag>
                      {msg.status === 'unread' && msg.to === 'cashier' && msg.toId === cashierId && (
                        <Badge status="processing" text="New" />
                      )}
                    </Space>
                  }
                  description={
                    <div style={{ marginTop: '8px' }}>
                      {msg.inReplyTo && (
                        <div style={{ 
                          background: 'var(--bg-tertiary)', 
                          padding: '8px', 
                          borderRadius: '4px',
                          borderLeft: '3px solid var(--primary-color)',
                          marginBottom: '8px'
                        }}>
                          <Text type="secondary" style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                            Replying to:
                          </Text>
                          <div style={{ marginTop: '4px' }}>
                            <Text style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                              {messages.find(m => m.id === msg.inReplyTo)?.message || 'Original message not found'}
                            </Text>
                          </div>
                        </div>
                      )}
                      <Text style={{ color: 'var(--lcolor)' }}>{msg.message}</Text>
                      <div style={{ marginTop: '8px' }}>
                        <Text type="secondary" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {new Date(msg.timestamp).toLocaleString()}
                        </Text>
                      </div>
                      {msg.from === 'admin' && (
                        <Button
                          size="small"
                          type="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            setReplyingTo(msg);
                            setReplyModalOpen(true);
                          }}
                        >
                          Reply
                        </Button>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        title="Reply to Admin"
        open={replyModalOpen}
        onCancel={() => {
          setReplyModalOpen(false);
          setReplyingTo(null);
          setReplyText('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setReplyModalOpen(false);
            setReplyingTo(null);
            setReplyText('');
          }}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleReply}
          >
            Send Reply
          </Button>
        ]}
      >
        {replyingTo && (
          <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>Replying to:</Text>
            <div><Text>{replyingTo.message}</Text></div>
          </div>
        )}
        <TextArea
          rows={4}
          placeholder="Type your reply..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          maxLength={500}
          showCount
        />
      </Modal>
    </>
  );
};

export default CashierMessaging;
